import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal, Stack, Text, Paper, Group, Button, Badge, TextInput,
  Select, NumberInput, Stepper, Alert, Box, SimpleGrid, Anchor,
  Code, Tabs, CopyButton, ActionIcon, Tooltip, Loader, Checkbox,
} from '@mantine/core';
import { Target, Check, Copy, Lightning, Lightbulb, WarningCircle } from '@phosphor-icons/react';
import api from '../utils/api';

const BUSINESS_PRESETS = [
  { id: 'saas', conversions: ['registration', 'purchase'] },
  { id: 'ecommerce', conversions: ['purchase', 'add_to_cart'] },
  { id: 'leadgen', conversions: ['lead', 'phone_call'] },
  { id: 'content', conversions: ['lead', 'page_view'] },
];

// Buduje wywołanie gtag do wklejenia w kodzie z gotowego snippetu <script>.
// Strony renderowane po stronie klienta nie mają strony potwierdzenia, więc
// oficjalny snippet "wklej na stronie dziękujemy" nie ma gdzie trafić.
const spaSnippetFrom = (eventCode) => {
  const sendTo = (eventCode || '').match(/'send_to':\s*'([^']+)'/);
  if (!sendTo) return null;
  return `// W handlerze udanej wysyłki formularza, PO odpowiedzi z API:
const res = await wyslijFormularz(dane);
if (res.ok) {
  window.gtag?.('event', 'conversion', {
    send_to: '${sendTo[1]}',
    value: 0.0,
    currency: 'PLN',
  });
}`;
};

export default function ConversionSetup({ opened, onClose, onCreated, isSpa = false, campaignId = null }) {
  const { t } = useTranslation();
  const [spaMode, setSpaMode] = useState(isSpa);
  const [step, setStep] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [existing, setExisting] = useState([]);
  const [loading, setLoading] = useState(true);

  // Selection
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [selectedTemplates, setSelectedTemplates] = useState([]);

  // Custom
  const [customName, setCustomName] = useState('');
  const [customCategory, setCustomCategory] = useState('DEFAULT');
  const [customCounting, setCustomCounting] = useState('ONE_PER_CLICK');
  const [customValue, setCustomValue] = useState('');

  // Creating
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!opened) return;
    setSpaMode(isSpa);
    setStep(0);
    setCreated([]);
    setError('');
    setSelectedPreset(null);
    setSelectedTemplates([]);
    setLoading(true);

    const scope = campaignId ? `?campaign_id=${campaignId}` : '';
    Promise.all([
      api.get('/ads/conversions/templates/'),
      api.get(`/ads/conversions/${scope}`),
    ]).then(([tplRes, existRes]) => {
      setTemplates(tplRes.data.templates || []);
      setExisting(existRes.data.actions || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [opened, isSpa, campaignId]);

  const handlePresetSelect = (preset) => {
    setSelectedPreset(preset);
    setSelectedTemplates(preset.conversions);
    setStep(1);
  };

  const toggleTemplate = (id) => {
    setSelectedTemplates(prev =>
      prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]
    );
  };

  const handleCreate = async () => {
    setCreating(true);
    setError('');
    const results = [];

    for (const tplId of selectedTemplates) {
      const tpl = templates.find(t => t.id === tplId);
      if (!tpl) continue;
      // Skip if already exists
      if (existing.some(e => e.name.toLowerCase() === tpl.name.toLowerCase())) {
        results.push({ ...tpl, skipped: true });
        continue;
      }
      try {
        const res = await api.post('/ads/conversions/create/', {
          name: tpl.name,
          category: tpl.category,
          counting_type: tpl.counting_type,
          default_value: tpl.default_value,
          ...(tpl.primary_for_goal === false ? { primary_for_goal: false } : {}),
          campaign_id: campaignId,
        });
        if (res.data.status === 'success') {
          results.push({ ...tpl, created: true, tracking_code: res.data.tracking_code });
        } else {
          results.push({ ...tpl, error: res.data.message });
        }
      } catch (err) {
        results.push({ ...tpl, error: err.response?.data?.message || t('conversions.setup.errorFallback') });
      }
    }

    // Custom conversion
    if (customName.trim()) {
      try {
        const res = await api.post('/ads/conversions/create/', {
          name: customName.trim(),
          category: customCategory,
          counting_type: customCounting,
          default_value: customValue || 0,
          campaign_id: campaignId,
        });
        if (res.data.status === 'success') {
          results.push({ name: customName.trim(), created: true, tracking_code: res.data.tracking_code });
        } else {
          results.push({ name: customName.trim(), error: res.data.message });
        }
      } catch (err) {
        results.push({ name: customName.trim(), error: err.response?.data?.message || t('conversions.setup.errorFallback') });
      }
    }

    setCreated(results);
    setCreating(false);
    setStep(2);
    if (onCreated) onCreated();
  };

  const existingNames = new Set(existing.map(e => e.name.toLowerCase()));

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <Target size={18} weight="bold" style={{ color: 'var(--mantine-color-brand-5)' }} />
          <Text fw={600}>{t('conversions.setup.title')}</Text>
        </Group>
      }
      size="lg"
      centered
    >
      {loading ? (
        <Stack align="center" py="xl">
          <Loader size="sm" color="brand" />
          <Text size="sm" c="dimmed">{t('conversions.setup.loading')}</Text>
        </Stack>
      ) : (
        <>
          <Stepper active={step} size="xs" color="brand" mb="md">
            <Stepper.Step label={t('conversions.setup.steps.businessType')} />
            <Stepper.Step label={t('conversions.setup.steps.selectConversions')} />
            <Stepper.Step label={t('conversions.setup.steps.done')} />
          </Stepper>

          {/* Step 0: Business type */}
          {step === 0 && (
            <Stack gap="sm">
              <Alert color="blue" icon={<Lightbulb size={16} />} mb="xs">
                <Text size="xs">
                  {t('conversions.setup.intro')}
                </Text>
              </Alert>

              {existing.filter(e => e.status === 'enabled').length > 0 && (() => {
                // Count and list must use the SAME filter. We only show ENABLED
                // actions, so the count is the number of enabled ones too (not the
                // total non-removed, which previously made "5" with only 3 listed).
                const enabled = existing.filter(e => e.status === 'enabled');
                return (
                <Paper p="sm" bg="green.0" radius="sm" withBorder style={{ borderColor: 'var(--mantine-color-green-3)' }}>
                  <Text size="xs" fw={500} c="green.8" mb={4}>{enabled.length === 1 ? t('conversions.setup.existingCountOne', { count: enabled.length }) : t('conversions.setup.existingCountMany', { count: enabled.length })}</Text>
                  <Group gap={4} wrap="wrap">
                    {enabled.slice(0, 6).map(e => (
                      <Badge key={e.id} size="xs" variant="light" color="green">{e.name}</Badge>
                    ))}
                  </Group>
                </Paper>
                );
              })()}

              <Text size="sm" fw={500}>{t('conversions.setup.chooseBusinessType')}</Text>
              <SimpleGrid cols={2} spacing="sm">
                {BUSINESS_PRESETS.map(preset => (
                  <Paper
                    key={preset.id}
                    withBorder
                    p="md"
                    radius="md"
                    style={{ cursor: 'pointer', transition: 'all 0.15s', borderColor: selectedPreset?.id === preset.id ? 'var(--mantine-color-brand-5)' : undefined }}
                    className="campaign-row"
                    onClick={() => handlePresetSelect(preset)}
                  >
                    <Text size="sm" fw={600} mb={4}>{t(`conversions.setup.presets.${preset.id}.label`)}</Text>
                    <Text size="xs" c="dimmed" lh={1.4}>{t(`conversions.setup.presets.${preset.id}.description`)}</Text>
                  </Paper>
                ))}
              </SimpleGrid>

              <Button variant="subtle" color="gray" size="xs" onClick={() => { setSelectedTemplates([]); setStep(1); }}>
                {t('conversions.setup.chooseManually')}
              </Button>
            </Stack>
          )}

          {/* Step 1: Select conversions */}
          {step === 1 && (
            <Stack gap="sm">
              <Text size="sm" fw={500}>{t('conversions.setup.chooseConversions')}</Text>

              {templates.map(tpl => {
                const alreadyExists = existingNames.has(tpl.name.toLowerCase());
                const isSelected = selectedTemplates.includes(tpl.id);
                return (
                  <Paper
                    key={tpl.id}
                    withBorder p="sm" radius="md"
                    style={{
                      cursor: alreadyExists ? 'default' : 'pointer',
                      opacity: alreadyExists ? 0.5 : 1,
                      borderColor: isSelected ? 'var(--mantine-color-brand-5)' : undefined,
                      backgroundColor: isSelected ? 'var(--mantine-color-brand-0)' : undefined,
                    }}
                    onClick={() => !alreadyExists && toggleTemplate(tpl.id)}
                  >
                    <Group justify="space-between" wrap="nowrap">
                      <Box>
                        <Group gap="xs">
                          <Text size="sm" fw={500}>{tpl.name}</Text>
                          {alreadyExists && <Badge size="xs" color="green" variant="light">{t('conversions.setup.badgeExists')}</Badge>}
                          {tpl.counting_type === 'EVERY' && <Badge size="xs" color="gray" variant="light">{t('conversions.setup.badgeEvery')}</Badge>}
                          {tpl.counting_type === 'ONE_PER_CLICK' && <Badge size="xs" color="gray" variant="light">{t('conversions.setup.badgeOnePerClick')}</Badge>}
                        </Group>
                        <Text size="xs" c="dimmed" mt={2}>{tpl.description}</Text>
                      </Box>
                      {isSelected && !alreadyExists && (
                        <Check size={18} weight="bold" style={{ color: 'var(--mantine-color-brand-5)', flexShrink: 0 }} />
                      )}
                    </Group>
                  </Paper>
                );
              })}

              {/* Custom conversion */}
              <Paper withBorder p="sm" radius="md">
                <Text size="xs" fw={500} mb="xs" c="dimmed">{t('conversions.setup.orAddCustom')}</Text>
                <Group gap="xs" wrap="nowrap">
                  <TextInput
                    placeholder={t('conversions.setup.customNamePlaceholder')}
                    value={customName}
                    onChange={e => setCustomName(e.target.value)}
                    size="xs"
                    style={{ flex: 1 }}
                  />
                  <Select
                    data={[
                      { value: 'PURCHASE', label: t('conversions.setup.categories.purchase') },
                      { value: 'SIGNUP', label: t('conversions.setup.categories.signup') },
                      { value: 'SUBMIT_LEAD_FORM', label: t('conversions.setup.categories.leadForm') },
                      { value: 'PAGE_VIEW', label: t('conversions.setup.categories.pageView') },
                      { value: 'DEFAULT', label: t('conversions.setup.categories.other') },
                    ]}
                    value={customCategory}
                    onChange={setCustomCategory}
                    size="xs"
                    w={130}
                  />
                </Group>
              </Paper>

              <Group justify="space-between">
                <Button variant="subtle" color="gray" size="xs" onClick={() => setStep(0)}>{t('conversions.setup.back')}</Button>
                <Button
                  color="brand" size="sm"
                  onClick={handleCreate}
                  loading={creating}
                  disabled={selectedTemplates.length === 0 && !customName.trim()}
                  leftSection={<Lightning size={14} />}
                >
                  {t('conversions.setup.createConversions', { count: selectedTemplates.filter(id => !existingNames.has((templates.find(tpl => tpl.id === id)?.name || '').toLowerCase())).length + (customName.trim() ? 1 : 0) })}
                </Button>
              </Group>
            </Stack>
          )}

          {/* Step 2: Done + tracking code */}
          {step === 2 && (
            <Stack gap="sm">
              <Alert color="green" icon={<Check size={16} />}>
                <Text size="sm" fw={500}>{t('conversions.setup.createdAlert')}</Text>
              </Alert>

              {created.map((c, i) => (
                <Paper key={i} withBorder p="sm" radius="md">
                  <Group justify="space-between" mb="xs">
                    <Text size="sm" fw={500}>{c.name}</Text>
                    {c.created && <Badge size="xs" color="green">{t('conversions.setup.badgeCreated')}</Badge>}
                    {c.skipped && <Badge size="xs" color="yellow">{t('conversions.setup.badgeExistsShort')}</Badge>}
                    {c.error && <Badge size="xs" color="red">{t('conversions.setup.badgeError')}</Badge>}
                  </Group>
                  {c.error && <Text size="xs" c="red">{c.error}</Text>}
                </Paper>
              ))}

              {created.some(c => c.created && c.tracking_code) && (() => {
                const firstCode = created.find(c => c.created && c.tracking_code)?.tracking_code;
                const globalTag = firstCode?.global_tag || '';
                const eventSnippets = created
                  .filter(c => c.created && c.tracking_code?.event_code)
                  .map(c => ({ name: c.name, code: c.tracking_code.event_code }));

                const CodeWithCopy = ({ label, code }) => (
                  <Box>
                    <Group justify="space-between" mb={4}>
                      <Text size="xs" fw={500}>{label}</Text>
                      <CopyButton value={code}>
                        {({ copied, copy }) => (
                          <Tooltip label={copied ? t('conversions.setup.copied') : t('conversions.setup.copy')} withArrow>
                            <ActionIcon size="sm" variant="subtle" color={copied ? 'green' : 'gray'} onClick={copy}>
                              {copied ? <Check size={14} /> : <Copy size={14} />}
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </CopyButton>
                    </Group>
                    <Code block style={{ fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{code}</Code>
                  </Box>
                );

                return (
                  <Paper withBorder p="md" radius="md" bg="blue.0">
                    <Text size="sm" fw={600} mb="xs">{t('conversions.setup.addCodeTitle')}</Text>
                    {isSpa && (
                      <Alert color="blue" variant="light" mb="sm" p="xs">
                        <Text size="xs" lh={1.5}>{t('conversions.setup.spaDetected')}</Text>
                      </Alert>
                    )}
                    <Text size="xs" c="dimmed" lh={1.5} mb="xs">
                      {spaMode
                        ? t('conversions.setup.spaInstructions')
                        : t('conversions.setup.addCodeInstructions')}
                    </Text>
                    <Checkbox
                      size="xs"
                      mb="sm"
                      checked={spaMode}
                      onChange={e => setSpaMode(e.currentTarget.checked)}
                      label={<Text size="xs" c="dimmed">{t('conversions.setup.spaToggle')}</Text>}
                    />
                    <Stack gap="sm">
                      {globalTag && <CodeWithCopy label={t('conversions.setup.globalTagLabel')} code={globalTag} />}
                      {eventSnippets.map((s, idx) => {
                        const spaCode = spaMode ? spaSnippetFrom(s.code) : null;
                        return spaCode ? (
                          <CodeWithCopy key={idx} label={t('conversions.setup.spaEventLabel', { name: s.name })} code={spaCode} />
                        ) : (
                          <CodeWithCopy key={idx} label={t('conversions.setup.eventCodeLabel', { name: s.name })} code={s.code} />
                        );
                      })}
                    </Stack>
                    {spaMode && (
                      <Alert color="yellow" variant="light" mt="sm" p="xs">
                        <Text size="xs" fw={600}>{t('conversions.setup.spaConsentTitle')}</Text>
                        <Text size="xs" lh={1.5}>{t('conversions.setup.spaConsentBody')}</Text>
                      </Alert>
                    )}
                    <Anchor href="/poradniki/conversions" target="_blank" size="xs" mt="sm" display="inline-block">
                      {t('conversions.setup.guideLink')}
                    </Anchor>
                  </Paper>
                );
              })()}

              <Group justify="flex-end">
                <Button color="brand" size="sm" onClick={onClose}>{t('conversions.setup.done')}</Button>
              </Group>
            </Stack>
          )}
        </>
      )}
    </Modal>
  );
}
